/**
 * Centralized RBAC Permissions and Roles Constants (Frontend)
 */

export const PERMISSIONS = Object.freeze({
  // Products
  PRODUCTS_READ: "products.read",
  PRODUCTS_CREATE: "products.create",
  PRODUCTS_UPDATE: "products.update",
  PRODUCTS_DELETE: "products.delete",
  PRODUCTS_BULK_UPLOAD: "products.bulk_upload",

  // Categories
  CATEGORIES_MANAGE: "categories.manage",

  // Masters
  MASTERS_SCHOOL: "masters.school",
  MASTERS_COLLEGE: "masters.college",
  MASTERS_COMPETITIVE: "masters.competitive",

  // Orders
  ORDERS_READ: "orders.read",
  ORDERS_UPDATE_STATUS: "orders.update_status",
  ORDERS_DISPATCH: "orders.dispatch",
  ORDERS_CANCEL: "orders.cancel",
  ORDERS_INVOICE: "orders.invoice",

  // Payments, Refunds & Returns
  PAYMENTS_READ: "payments.read",
  REFUNDS_READ: "refunds.read",
  REFUNDS_CREATE: "refunds.create",
  REFUNDS_APPROVE: "refunds.approve",
  RETURNS_READ: "returns.read",
  RETURNS_MANAGE: "returns.manage",

  // Vendors & Customers
  VENDORS_READ: "vendors.read",
  VENDORS_APPROVE: "vendors.approve",
  VENDORS_UPDATE: "vendors.update",
  VENDORS_DELETE: "vendors.delete",
  CUSTOMERS_READ: "customers.read",
  CUSTOMERS_UPDATE: "customers.update",

  // Inventory & Stock
  INVENTORY_READ: "inventory.read",
  INVENTORY_ADJUST: "inventory.adjust",
  PURCHASES_MANAGE: "purchases.manage",

  // Reports
  REPORTS_INVENTORY: "reports.inventory",
  REPORTS_PURCHASE: "reports.purchase",
  REPORTS_ORDERS: "reports.orders",
  REPORTS_CANCELLED_ORDERS: "reports.cancelled_orders",
  REPORTS_LOW_STOCK: "reports.low_stock",
  REPORTS_REVENUE: "reports.revenue",

  // Banners & Marketing
  BANNERS_MANAGE: "banners.manage",

  // Roles, Permissions & Logs
  ROLES_MANAGE: "roles.manage",
  USERS_READ: "users.read",
  USERS_MANAGE: "users.manage",
  USERS_MANAGE_ROLES: "users.manage_roles",
  ACTIVITY_LOGS_READ: "activity_logs.read",
});

export const ROLES = Object.freeze({
  SUPER_ADMIN: "super_admin",
  PRODUCT_MANAGER: "product_manager",
  ORDER_MANAGER: "order_manager",
  CUSTOMER_SUPPORT: "customer_support",
  VENDOR_MANAGER: "vendor_manager",
  INVENTORY_MANAGER: "inventory_manager",
  CUSTOMER: "customer",
  VENDOR: "vendor",
  DELIVERY_PARTNER: "delivery_partner",
  ADMIN: "admin",
});

export const ROLE_DISPLAY_NAMES = Object.freeze({
  [ROLES.SUPER_ADMIN]: "Super Admin",
  [ROLES.PRODUCT_MANAGER]: "Product Manager",
  [ROLES.ORDER_MANAGER]: "Order Manager",
  [ROLES.CUSTOMER_SUPPORT]: "Customer Support",
  [ROLES.VENDOR_MANAGER]: "Vendor Manager",
  [ROLES.INVENTORY_MANAGER]: "Inventory Manager",
  [ROLES.CUSTOMER]: "Customer",
  [ROLES.VENDOR]: "Vendor",
  [ROLES.DELIVERY_PARTNER]: "Delivery Partner",
  [ROLES.ADMIN]: "Admin",
});

export const MODULE_GROUPS = Object.freeze({
  PRODUCTS: [
    PERMISSIONS.PRODUCTS_READ,
    PERMISSIONS.PRODUCTS_CREATE,
    PERMISSIONS.PRODUCTS_UPDATE,
    PERMISSIONS.PRODUCTS_DELETE,
    PERMISSIONS.PRODUCTS_BULK_UPLOAD,
  ],
  CATEGORIES: [PERMISSIONS.CATEGORIES_MANAGE],
  MASTERS: [
    PERMISSIONS.MASTERS_SCHOOL,
    PERMISSIONS.MASTERS_COLLEGE,
    PERMISSIONS.MASTERS_COMPETITIVE,
  ],
  ORDERS: [
    PERMISSIONS.ORDERS_READ,
    PERMISSIONS.ORDERS_UPDATE_STATUS,
    PERMISSIONS.ORDERS_DISPATCH,
    PERMISSIONS.ORDERS_CANCEL,
    PERMISSIONS.ORDERS_INVOICE,
  ],
  PAYMENTS: [
    PERMISSIONS.PAYMENTS_READ,
    PERMISSIONS.REFUNDS_READ,
    PERMISSIONS.REFUNDS_CREATE,
    PERMISSIONS.REFUNDS_APPROVE,
  ],
  RETURNS: [
    PERMISSIONS.RETURNS_READ,
    PERMISSIONS.RETURNS_MANAGE,
  ],
  VENDORS: [
    PERMISSIONS.VENDORS_READ,
    PERMISSIONS.VENDORS_APPROVE,
    PERMISSIONS.VENDORS_UPDATE,
    PERMISSIONS.VENDORS_DELETE,
  ],
  CUSTOMERS: [PERMISSIONS.CUSTOMERS_READ, PERMISSIONS.CUSTOMERS_UPDATE],
  INVENTORY: [
    PERMISSIONS.INVENTORY_READ,
    PERMISSIONS.INVENTORY_ADJUST,
    PERMISSIONS.PURCHASES_MANAGE,
  ],
  REPORTS: [
    PERMISSIONS.REPORTS_INVENTORY,
    PERMISSIONS.REPORTS_PURCHASE,
    PERMISSIONS.REPORTS_ORDERS,
    PERMISSIONS.REPORTS_CANCELLED_ORDERS,
    PERMISSIONS.REPORTS_LOW_STOCK,
    PERMISSIONS.REPORTS_REVENUE,
  ],
  SETTINGS_AND_ROLES: [
    PERMISSIONS.BANNERS_MANAGE,
    PERMISSIONS.ROLES_MANAGE,
    PERMISSIONS.USERS_READ,
    PERMISSIONS.USERS_MANAGE,
    PERMISSIONS.USERS_MANAGE_ROLES,
    PERMISSIONS.ACTIVITY_LOGS_READ,
  ],
});
